import React from "react";
import { useFetchRefundsHistory } from "../../util/fetchUserUtil";
import API_URL from "../../config/apiConfig";
import { apiGet } from "../../util/axiosUtil";
import MutationHandler from "../../util/MutationHandler";

const getBarColor = (value, thresholds) => {
  if (value >= thresholds.red) return "bg-red-600";
  if (value >= thresholds.yellow) return "bg-yellow-500";
  return "bg-green-600";
};

const RefundAccept = ({ onCancel, data, onSuccess }) => {
  const {
    data: refundHistory,
    isLoading,
    isError,
    error: fetchError,
  } = useFetchRefundsHistory(data.parent);

  const weeklyThresholds = { red: 2, yellow: 1 };
  const monthlyThresholds = { red: 10, yellow: 5 };
  const yearlyThresholds = { red: 80, yellow: 40 };
  const totalThresholds = { red: 100, yellow: 50 };

  if (isLoading) {
    return <div className="text-center text-gray-600">Loading...</div>;
  }

  if (isError) {
    return (
      <div className="text-center text-red-600">
        Error: {fetchError?.message || "Failed to load refund history"}
      </div>
    );
  }

  return (
    <MutationHandler
      mutationFn={async (action) =>
        await apiGet(`${API_URL}/api/refunds/${action}/${data.id}`)
      }
      onCancel={onCancel}
      onSuccess={onSuccess}
    >
      {(mutation) => (
        <div className="bg-white dark:bg-zinc-900 rounded-lg pb-4 px-4 max-w-lg mx-auto text-base text-zinc-900 dark:text-white">
          <h2 className="text-sm font-semibold mb-4">
            Parent Refund Statistics: {data.parent}
          </h2>

          {/* Refund Status Bars */}
          {[
            {
              label: "Total Refunds",
              value: refundHistory.data.totalRefunds,
              thresholds: totalThresholds,
            },
            {
              label: "Weekly",
              value: refundHistory.data.lastWeekRefunds,
              thresholds: weeklyThresholds,
            },
            {
              label: "Monthly",
              value: refundHistory.data.lastMonthRefunds,
              thresholds: monthlyThresholds,
            },
            {
              label: "Yearly",
              value: refundHistory.data.lastYearRefunds,
              thresholds: yearlyThresholds,
            },
            {
              label: "Refunded",
              value: refundHistory.data.totalRefunded,
              thresholds: totalThresholds,
            },
            {
              label: "Rejected",
              value: refundHistory.data.totalRejected,
              thresholds: totalThresholds,
            },
            {
              label: "Pending",
              value: refundHistory.data.totalPending,
              thresholds: totalThresholds,
            },
          ].map(({ label, value, thresholds }) => (
            <div key={label} className="mb-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm">{label}:</span>
                <span className="text-sm font-semibold">{value}</span>
              </div>
              <div className="w-full bg-gray-200 h-2 rounded-full">
                <div
                  className={`h-2 rounded-full ${getBarColor(
                    value,
                    thresholds
                  )}`}
                  style={{
                    width: `${Math.min((value / thresholds.red) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>
          ))}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => mutation.mutate("accept")}
              disabled={mutation.isPending}
              className="bg-red-600 w-full text-white px-4 py-2 rounded-lg hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              Accept
            </button>

            <button
              type="button"
              onClick={() => mutation.mutate("reject")}
              disabled={mutation.isPending}
              className="bg-blue-600 w-full text-white px-6 py-2 rounded-lg hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Reject
            </button>

            <button
              type="button"
              onClick={onCancel}
              className="bg-gray-200 w-full text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Cancel
            </button>
          </div>

          {/* Feedback Messages */}
          {/* {mutation.isError && (
            <p className="mt-4 text-sm text-red-600">
              {mutation.error?.response?.data?.message || "An error occurred."}
            </p>
          )}

          {mutation.isSuccess && (
            <p className="mt-4 text-sm text-green-600">
              {mutation.data?.data?.message ||
                `Refund ${
                  mutation.variables === "accept" ? "accepted" : "rejected"
                } successfully.`}
            </p>
          )} */}
        </div>
      )}
    </MutationHandler>
  );
};

export default React.memo(RefundAccept);
